import React, { FC, Fragment, useEffect, useRef, useState, useContext } from "react";
import { Button, Flex, Input, notification, TableProps, Tag, Tooltip, Select, Space, App, Typography } from "antd";
import { useTranslation } from 'react-i18next';
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import { CountingStateEnum } from "@/types/inventory";
import { CountOrder, CountOrderStateEnum } from "@/types/inventoryV2";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName, hasPermission } from "@/utils/user";
import dayjs from "dayjs";
import { countList } from "@/apis/inventoryv2";
import { CommonListParams } from "@/types/common";
import CountingOrderAdd from "./components/Add";
import CountingOrderDetail from "./components/Detail";
import CountAddV2 from "../components/CountAddV2";
import { countingStatusV2 } from "../../utils";
import { useSelector } from "react-redux";
import { RootState } from "@/store/index";
import { Org } from "@/types/org";
import errorCodes from "@/apis/error";
import { getStorage } from "@/utils/storage";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { dateFormat } from "@/utils/format";
import { User, UserPermissionEnum } from "@/types/user";
import SText from "@/styles/SText";
import BatchImport from "../components/BatchImport";
import { getLocationIdx } from "@/utils/wLocation";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface CountingFilters {
  keyword: string;
}

const INITIAL_COUNTING_FILTERS: CountingFilters = {
  keyword: '',
};

const CountingView: FC = () => {
  const { t } = useTranslation();
  const [notificationApi, contextHolder] = notification.useNotification({ placement: 'top' });
  const { notification: appNotification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const curOrg: Org = useSelector((state: RootState) => state.orgReducer.curOrg);
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);
  const orgId = getStorage("curOrgId");

  const [isOpenCountAddV2, setIsOpenCountAddV2] = useState<boolean>(false);

  const [rowInfo, setRowInfo] = useState<CountOrder | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<CountingFilters>(INITIAL_COUNTING_FILTERS);
  const { keyword } = draftFilters;


  const [list, total, loading, { fetchList, updateItem, addItem, addItems, rmItem, }] = useDataList<CountOrder, [CommonListParams]>({ fetchApi: countList });


  // ✅ 取消请求：记录当前进行中的请求控制器
  const abortControllerRef = useRef<AbortController | null>(null);

  // 组件卸载时取消进行中的请求
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_COUNTING_FILTERS);
    fetch(0, nextFilters);
    // 首次加载只执行一次，筛选提交由事件显式触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceFilters]);

  const handleExportAll = async (): Promise<CountOrder[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allCountData: CountOrder[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore && (curOrg.id || orgId)) {
        const response = await countList({
          body: appliedFilters.keyword,
          pagination: { offset, take: batchSize }
        });

        if (response.body && response.body.length > 0) {
          allCountData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allCountData.length /
                (response.pagination?.size || allCountData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);

          if (response.body.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setExportProgress(100);
      return allCountData;
    } catch (error) {
      appNotification.error({
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const countingColumns = [
    { key: 'number', label: t('counting.num') },
    { key: 'creator', label: t('counting.creator') },
    { key: 'createdAt', label: t('counting.createdAt') },
    { key: 'locationNumber', label: t('warehouse.locationCode') },
    { key: 'locationIndex', label: t('warehouse.locationIndex') },
    { key: 'orderState', label: t('counting.invStatus') },
  ];

  const countingDataMapper = (item: CountOrder, t: any) => {
    const stateInfo = countingStatusV2.find(st => st.value === item.orderState);

    return {
      [t('counting.num')]: item.number || '',
      [t('counting.creator')]: getUserName(item.creator) || '',
      [t('counting.createdAt')]: item.createdAt ? dateFormat(dayjs(item.createdAt), true) : '',
      [t('warehouse.locationCode')]: item.location?.number || '',
      [t('warehouse.locationIndex')]: item.location ? getLocationIdx(item.location) : '',
      [t('counting.invStatus')]: stateInfo?.label || '',
    };
  };

  const columns: TableProps<CountOrder>["columns"] = [
    {
      title: t('counting.num'),
      dataIndex: "number",
      key: "number",
      sorter: (a, b) => {
        const valA = a?.number ?? "";
        const valB = b?.number ?? "";
        return valA.localeCompare(valB);
      },
      width: 220,
      fixed: "left",
      render(value, record, index) {
        return (
          <Typography.Link onClick={() => detailClick(record)} >{record.number}</Typography.Link>
        )
      },
    },
    {
      title: t('counting.creator'),
      dataIndex: "creator",
      key: "creator",
      render: (creator) => (
        creator ? <>
          <WMSAvatar
            avatarId={creator.avatar}
            uniChar={creator.id}
            creator={creator}
            alt={getUserAvatarTip(creator)}
            style={{ width: '30px', height: '30px', marginRight: '6px' }}
          />
          <span>{getUserName(creator)}</span>
        </>
          : <>-</>
      ),
    },
    {
      title: t('counting.createdAt'),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => dateFormat(dayjs(createdAt), true),
    },
    {
      title: t("warehouse.locationCode"),
      dataIndex: ["location", "number"],
      width: 260,
      sorter: (a, b) => {
        const valA = a?.location?.number ?? "";
        const valB = b?.location?.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => <SText text={record.location?.number ?? ''} />
    },
    {
      title: t("warehouse.locationIndex"),
      dataIndex: "location",
      render: (_, record) => record.location && <SText text={getLocationIdx(record.location)} />

    },
    {
      title: t('counting.invStatus'),
      dataIndex: "orderState",
      key: "orderState",
      fixed: "right",
      render: (orderState, { id }) => {
        let info = countingStatusV2.find((item) => item.value === orderState);
        return (
          <Tag key={id} color={info?.colorKey}>{`${info?.label ?? ""}`}</Tag>
        )
      }
    },
    {
      title: t('counting.opt'),
      dataIndex: "operation",
      key: "operation",
      fixed: "right",
      width: 140,
      render: (_, item) => (
        <Button type="link" onClick={(e) => { e.stopPropagation(); detailClick(item); }}>{t('counting.optDetail')}</Button>
      ),
    }
  ];

  const fetch = (offset: number, filters: CountingFilters) => {
    if (curOrg.id || orgId) {
      const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
      // ✅ 取消上一次还在进行中的请求，避免旧响应覆盖新响应
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      fetchList({
        body: filters.keyword,
        pagination: { offset, take },
        controller // ✅ 传递 controller 用于取消请求（含 useDataList 的 Esc 中止）
      })
        .then(() => { })
        .catch((err) => notificationApi.error({ message: errorCodes[err] || err }));
    } else {
      notificationApi.warning({ message: t('org.selectOrg') })
    }
  };

  const detailClick = (row: CountOrder) => {
    setRowInfo(row);
  }

  const detailHandleOk = () => {
    if (rowInfo) {
      updateItem({ ...rowInfo, orderState: CountOrderStateEnum.DONE })
    }
    detailHandleCancel();
  }

  const detailHandleCancel = (type: "UODATA" | "DEL" | "CLOSE" = "CLOSE") => {
    setRowInfo(null);
    if (type === "DEL") {
      const info = { ...rowInfo };
      rmItem(info.id ?? '');
      setRowInfo(null);
    }
    if (type === "CLOSE") setRowInfo(null);
  }

  const getHasPermission = (key: UserPermissionEnum) => {
    return hasPermission(curUser, [UserPermissionEnum.ADMIN, key], curOrg?.id ?? '') || hasPermission(curUser, [UserPermissionEnum.ADMIN])
  }

  const batchImportSucc = (orders: CountOrder[]) => {
    addItems(orders);
    notification.success({
      placement: 'top',
      message: t('counting.importSuccess'),
      description: `${orders.map(or => or.number).join(', ')}${t('counting.countingCompleted')}`
    })
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_COUNTING_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const headerChildren = (
    <Flex justify="space-between" align="center" style={{ width: "100%" }}>
      <Flex gap={10}>
        {curOrg && getHasPermission(UserPermissionEnum.ACT_STORE_COUNTING) &&
          <Space>
            <Button type="primary" onClick={() => setIsOpenCountAddV2(true)}>{t('counting.startInv')}</Button>
            <BatchImport onSuccess={batchImportSucc} />
          </Space>
        }
        <ExportExcelButton
          list={list}
          columns={countingColumns}
          defaultSelectedColumns={[t('counting.num'), t('counting.creator'), t('counting.createdAt')]}
          dataMapper={countingDataMapper}
          fileName={`${t('counting.countingHistory')}.xlsx`}
        />
        <ExportExcelButton
          isAll
          loadData={handleExportAll}
          columns={countingColumns}
          defaultSelectedColumns={[t('counting.num'), t('counting.creator'), t('counting.createdAt')]}
          dataMapper={countingDataMapper}
          fileName={`${t('counting.countingHistoryAll')}.xlsx`}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <Input
              style={{ width: 200 }}
              value={keyword}
              onChange={event => updateDraftFilters({ keyword: event.target.value })}
              onPressEnter={submit}
              placeholder={t('order.searchPl')}
            />
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      {contextHolder}
      <STableLayout
        loading={loading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />

      {rowInfo && <CountingOrderDetail
        id={rowInfo.id}
        open={true}
        handleOk={detailHandleOk}
        handleCancel={detailHandleCancel}
      />}

      <CountAddV2
        open={isOpenCountAddV2}
        onClose={(delOrder) => {
          console.log(delOrder);

          if (delOrder) {
            setIsOpenCountAddV2(false);
            setRowInfo(null);
            rmItem(delOrder.id)
          } else setIsOpenCountAddV2(false);
        }}
        onSuccess={(order, type) => {
          if (type === "ADD") addItem(order)
          if (type === "UPDATE") updateItem(order)
        }}
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default CountingView;
